// Copyright (c) 2020-2024 Visual Intelligence LP. All rights reserved.
//
import * as React from 'react';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { observer } from 'mobx-react';
import { computed } from 'mobx';
import { withStyles } from "tss-react/mui";
import container from '@core/di';
import Config from '@core/config';
import AssetsIcon from '@shared/icons/Assets';
import AssetsStore from '@shared/stores/assets';
import OrdersStore from '@shared/stores/orders';
import { SortingType } from '@shared/constants/services';
import Asset from '@shared/models/asset';
import { CustomerDTO } from '@shared/models/customers';
import { getQueries, setQueries } from '@shared/utils/common';
import Flex from '@shared/components/Flex';
import Field from '@shared/components/Form/components/Field';
import Autocomplete, { Option, normalize } from '@shared/components/Autocomplete';
import Message from '@shared/components/Message';
import { ManageMode } from '@shared/components/Form';
import ExpansionPanel from '@shared/components/ExpansionPanel';
import AssetManagement from '@modules/Private/submodules/Orders/components/Asset/AssetManagement';
import { ORDER_FIELDS } from '@shared/constants/order';
import { TOWER_FIELDS } from '@shared/constants/asset';
import OrderEntities from './components/OrderEntities';
import ROUTES from '@shared/constants/routes';

export interface AssetDeliverablesProps extends RouteComponentProps {
  className?: string;
  initializeOrderFormLeaveConfirmation: () => any;
}

@observer
class AssetDeliverables extends React.Component<AssetDeliverablesProps> {
  private assetsStore = container.get<AssetsStore>(AssetsStore.diToken);
  private ordersStore = container.get<OrdersStore>(OrdersStore.diToken);
  private config = container.get<Config>(Config.diToken);

  @computed get customerId(): CustomerDTO['id'] {
    const data = this.ordersStore.orderDetailsForm.values();

    return data[ORDER_FIELDS.customerId];
  }

  @computed get options(): Array<Option> {
    return normalize(this.assetsStore.list);
  }

  @computed get selectedOptions(): Array<Option['id']> {
    return this.ordersStore.assets.map(({ id }) => id);
  }

  private handleChange = (id: Asset['id']) => {
    const matchedAsset = this.assetsStore.list.find((asset) => asset.id == id);

    if (matchedAsset) {
      this.ordersStore.setAsset({
        id: matchedAsset.id,
        type: matchedAsset.type,
        name: matchedAsset.name
      });
    }
  }

  private handleAddNewAsset = () => {
    setQueries({ assetManageMode: ManageMode.create });
  }

  private handleInputChange = (searchKeyword: string) => {
    this.assetsStore.getList({
      pagination: { page: 0, pageSize: this.config.get().autocomplete.itemsCount },
      sorting: { type: SortingType.asc, field: TOWER_FIELDS.name },
      filtering: { searchKeyword, customerId: this.customerId, isBlocked: false }
    });
  }

  private handleClearOptions() {
    this.assetsStore.resetList;
  }

  render() {
    const { initializeOrderFormLeaveConfirmation } = this.props;
    const classes = withStyles.getClasses(this.props) as Record<string, any>;
    const queries = getQueries();
    const isOpenManagement = Boolean(queries.assetManageMode);

    console.log("********** Check AssetDelis:", this.props);

    return (
      <>
        <AssetManagement
          open={isOpenManagement}
          manageMode={queries.assetManageMode as ManageMode}
          customerId={this.customerId}
          formComponentProps={{
            closeLocation: { pathname: `${ROUTES.private.orders}/${ManageMode.create}` },
            onUnmount: initializeOrderFormLeaveConfirmation
            // ^^^ Form component calls unblock() when closing => 
            // thus terminates history.block handler which was declared in OrderCreating component,
            // there is no way to have multiple history.block handlers https://github.com/ReactTraining/history/issues/423
          }}
        />
        <Flex
          direction="column"
          alignItems="center"
        >
          <Flex alignItems="center">
            <Field
              classes={{ root: classes.autocomplete }}
              label="Search or create asset"
              component={(
                <Autocomplete
                  disableClearable
                  withCreateNewButton
                  multiple={false}
                  placeholder="Enter asset name"
                  options={this.options}
                  selectedOptions={this.selectedOptions}
                  loading={this.assetsStore.loading.list}
                  createButtonText="Create asset"
                  selectedTooltipText="This asset is already in the order."
                  clearOptions={this.handleClearOptions.bind(this)}
                  onCreateClick={this.handleAddNewAsset.bind(this)}
                  onValueChange={this.handleChange.bind(this)}
                  onInputChange={this.handleInputChange.bind(this)}
                  testAttributeArgs={{
                    module: 'Orders',
                    elementDescription: 'assets',
                  }}
                />
              )}
            />
          </Flex>
          {this.ordersStore.assets.length
            ? (
              <div className={classes.assetList}>
                {this.ordersStore.assets.map((asset) => (
                  <ExpansionPanel
                    removable
                    defaultExpanded
                    key={asset.id}
                    id={asset.id}
                    title={asset.name}
                    testAttributeArgs={{
                      module: 'Orders',
                      elementDescription: 'asset-with-entities',
                    }}
                    classes={{
                      root: classes.expansionPanel,
                    }}
                    onRemove={this.ordersStore.removeAsset}
                  >
                    <OrderEntities
                      data={{ asset, deliverables: this.ordersStore.deliverables[asset.id] }}
                      initializeOrderFormLeaveConfirmation={initializeOrderFormLeaveConfirmation}
                    />
                  </ExpansionPanel>
                ))}
              </div>
            )
            : (
              <Message
                classes={{ root: classes.notFoundMessage }}
                icon={<AssetsIcon />}
                heading="There are no assets"
                subheading="Please add or create at least one asset to continue"
              />
            )}
        </Flex>
      </>
    );
  }
}

const AssetDeliverablesStyled = withStyles(
  AssetDeliverables, 
  (theme, props) => ({
    "root": {},
    "autocomplete": {
      width: 536,
    },
    "notFoundMessage": {
      width: 'auto',
      flexGrow: 1,
      height: '60vh',
    },
    "assetList": {
      width: 795,
      marginTop: theme.spacing(8),
      paddingRight: theme.spacing(2),
      height: 'calc(100vh - 288px)',
      overflow: 'auto',
    },
    "expansionPanel": {
      marginBottom: '8px !important',
    }
  })
);

export default withRouter(AssetDeliverablesStyled);